import { resolveSessionParentPathForAgentProcess } from '../../../paths/session-layout-paths.js';
import { localAgentErrorSuggestsAuth } from '../../../lib/local-agent-auth.js';
import type { BridgeToApiPromptResultMessage } from '../../../types/bridge-to-api-messages.js';
import { ensureAcpClient } from '../ensure-acp-client.js';
import { sendPromptToAgent } from '../prompts/send-prompt-to-agent.js';
import type { AcpManagerContext } from './acp-manager-context.js';
import { getAcpSessionAgentState } from './get-acp-agent-state.js';
import { handlePendingPromptCancel } from './handle-pending-prompt-cancel.js';
import type { HandlePromptOptions } from './types.js';
import type { PromptRunContext } from './resolve-prompt-run-context.js';

export async function runAcpPrompt(
  ctx: AcpManagerContext,
  runCtx: PromptRunContext,
  opts: HandlePromptOptions,
): Promise<void> {
  const {
    promptText,
    promptId,
    sessionId,
    mode,
    agentConfig,
    sessionParentPath,
    sendResult,
    sendSessionUpdate,
    followUpCatalogPromptId,
    cloudApiBaseUrl,
    getCloudAccessToken,
    e2ee,
    attachments,
    isNewSession,
  } = opts;
  const { activeRunId, activeAcpAgentKey, activeAcpSessionAgentKey, preferredForPrompt } = runCtx;

  const acpAgentState = getAcpSessionAgentState(ctx, activeAcpSessionAgentKey);
  const handle = await ensureAcpClient({
    state: acpAgentState,
    acpAgentKey: activeAcpAgentKey,
    preferredAgentType: preferredForPrompt,
    mode,
    agentConfig: agentConfig ?? null,
    sessionParentPath,
    resolveRouting: () => ctx.promptRouting.resolveRouting(activeAcpSessionAgentKey),
    cloudSessionId: sessionId,
    bridgeAccessPort: ctx.getBridgeAccessPort(),
    sendSessionUpdate,
    sendRequest: sendSessionUpdate,
    log: ctx.log,
    reportAgentCapabilities: ctx.reportAgentCapabilities,
  });

  if (!handle) {
    const errMsg =
      acpAgentState.lastAcpStartError ||
      'No agent configured. Register local agents on this bridge in the app.';
    const evaluated = Boolean(preferredForPrompt && errMsg.trim());
    const suggestsAuth = evaluated
      ? localAgentErrorSuggestsAuth(preferredForPrompt!, errMsg)
      : false;
    const auth =
      suggestsAuth && preferredForPrompt
        ? { agentAuthRequired: true as const, agentType: preferredForPrompt }
        : {};
    sendResult({
      type: 'prompt_result',
      id: promptId,
      ...(sessionId ? { sessionId } : {}),
      runId: activeRunId,
      success: false,
      error: errMsg,
      ...auth,
    });
    return;
  }

  if (!ctx.promptRouting.isRegisteredRun(activeRunId)) {
    return;
  }

  const cancelled = await handlePendingPromptCancel({
    ctx,
    activeRunId,
    promptId,
    sessionId,
    handle,
    sendResult,
  });
  if (cancelled) return;

  ctx.promptRouting.setStreamingRunId(activeAcpSessionAgentKey, activeRunId);
  try {
    await sendPromptToAgent({
      handle,
      promptText,
      promptId,
      sessionId,
      runId: activeRunId,
      agentType: preferredForPrompt,
      agentCwd: resolveSessionParentPathForAgentProcess(sessionParentPath),
      sendResult,
      sendSessionUpdate,
      log: ctx.log,
      followUpCatalogPromptId,
      cloudApiBaseUrl,
      getCloudAccessToken,
      e2ee,
      attachments,
      isNewSession,
    });
  } finally {
    ctx.promptRouting.clearStreamingRunId(activeAcpSessionAgentKey, activeRunId);
  }
}
